import type { CustomAttributeData } from '@risksmart-app/domain/src/types/common.types';
import type { ConsequenceType } from '@risksmart-app/domain/src/types/consts/consequence-type';
import type { CostType } from '@risksmart-app/domain/src/types/consts/cost-type';
import type {
  UpdateConsequenceMutation,
  UpdateConsequenceMutationVariables,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import { UpdateConsequenceDocument } from '@risksmart-app/web-graphql-client/generated/graphql';

import { createMutationHook } from '@/utils/hook-factories';

type UpdateConsequenceTRPCInput = {
  Id: string;
  ParentIssueId: string;
  Title: string;
  Description?: string | null;
  Criticality?: number | null;
  CostType: CostType;
  CostValue: number;
  Type?: ConsequenceType | null;
  CustomAttributeData?: CustomAttributeData | null;
  OriginalTimestamp: string;
};

export const useUpdateConsequence = createMutationHook({
  name: 'updateConsequence',
  mutationOptions: (trpc, onSuccess) =>
    trpc.frontend.consequence.update.mutationOptions({ onSuccess }),
  mapInput: (
    variables: UpdateConsequenceMutationVariables
  ): UpdateConsequenceTRPCInput => ({
    Id: variables.Id ?? '',
    ParentIssueId: variables.ParentIssueId ?? '',
    Title: variables.Title ?? '',
    Description: variables.Description,
    Criticality: variables.Criticality,
    CostType: variables.CostType!,
    CostValue: variables.CostValue ?? 0,
    Type: variables.Type,
    CustomAttributeData: variables.CustomAttributeData ?? null,
    OriginalTimestamp: variables.OriginalTimestamp ?? '',
  }),
  invalidate: (trpc) => [
    trpc.frontend.consequence.register.queryKey(),
    trpc.frontend.consequence.consequenceById.queryKey(),
    trpc.frontend.consequence.getByParentIssueId.queryKey(),
  ],
  mapResponse: (): UpdateConsequenceMutation => ({
    update_consequence: {
      __typename: 'consequence_mutation_response',
      affected_rows: 1,
    },
  }),
  graphqlDocument: UpdateConsequenceDocument,
  graphqlVariables: (variables: UpdateConsequenceMutationVariables) => ({
    ...variables,
  }),
  validateGraphqlResponse: (data) => {
    if (data.update_consequence?.affected_rows !== 1) {
      throw new Error(
        'Records not updated. Record may have been updated by another user'
      );
    }
  },
  evictFields: ['consequence', 'consequence_aggregate'],
});
